/**
 * Navbar.jsx — Top navigation bar with Profile dropdown and logout.
 *
 * Structure:
 *  - Logo / Brand
 *  - Role chip
 *  - Notification bell
 *  - Profile dropdown  (Matched Requirements / My Requirements / My Deals /
 *                        Trust & Penalties / Payout Profile)
 *  - Upload KYC        (top-level, shown only when KYC is incomplete)
 *  - Logout            (top-level)
 *  - Mobile: hamburger drawer with the same hierarchy
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

// ── Icons ────────────────────────────────────────────────────────────────────
import AccountCircleIcon      from '@mui/icons-material/AccountCircle';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import AccountBalanceIcon     from '@mui/icons-material/AccountBalance';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import ExpandLessIcon         from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import GavelOutlinedIcon      from '@mui/icons-material/GavelOutlined';
import HandshakeOutlinedIcon  from '@mui/icons-material/HandshakeOutlined';
import ListAltIcon            from '@mui/icons-material/ListAlt';
import LogoutIcon             from '@mui/icons-material/Logout';
import MenuIcon               from '@mui/icons-material/Menu';
import NotificationsIcon      from '@mui/icons-material/Notifications';
import SecurityIcon           from '@mui/icons-material/Security';
import UploadFileIcon         from '@mui/icons-material/UploadFile';
import LightModeIcon          from '@mui/icons-material/LightMode';
import DarkModeIcon           from '@mui/icons-material/DarkMode';


import { apiClient, clearSession, getAccessToken } from '../utils/auth';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build the profile menu items for a given role. */
function buildProfileItems(role, anonId) {
  const items = [];

  if (!role) return items;

  items.push({
    label: 'Profile Settings',
    icon: <AccountCircleIcon fontSize="small" />,
    path: '/profile/settings',
  });

  if (role === 'BUYER') {
    items.push({
      label: 'My Requirements',
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      path: '/buyer/requirements',
    });
    items.push({
      label: 'My Deals',
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      path: '/deals/mine',
    });
    items.push({
      label: 'KYC Verification',
      icon: <SecurityIcon fontSize="small" />,
      path: '/profile/settings?verify=true',
    });
    items.push({
      label: 'Trust & Penalties',
      icon: <GavelOutlinedIcon fontSize="small" />,
      path: '/account/trust',
    });
  }

  if (role === 'BIDDER') {
    items.push({
      label: 'Matched Requirements',
      icon: <ListAltIcon fontSize="small" />,
      path: '/bidder/requirements',
    });
    items.push({
      label: 'My Deals',
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      path: '/deals/mine',
    });
    if (anonId) {
      items.push({
        label: 'Preview Anonymous Profile',
        icon: <AccountCircleIcon fontSize="small" />,
        path: `/buyer/bidders/${anonId}`,
      });
    }
    items.push({
      label: 'My Earnings',
      icon: <EmojiEventsIcon fontSize="small" />,
      path: '/bidder/earnings',
    });
    items.push({
      label: 'KYC Verification',
      icon: <SecurityIcon fontSize="small" />,
      path: '/profile/settings?verify=true',
    });
    items.push({
      label: 'Trust & Penalties',
      icon: <GavelOutlinedIcon fontSize="small" />,
      path: '/account/trust',
    });
    items.push({
      label: 'Payout Profile',
      icon: <AccountBalanceIcon fontSize="small" />,
      path: '/account/payout-profile',
    });
  }


  if (role === 'ARBITRATOR') {
    items.push({
      label: 'Submit KYC / Profile',
      icon: <UploadFileIcon fontSize="small" />,
      path: '/profile/settings?verify=true',
    });
    items.push({
      label: 'My Availability',
      icon: <AccessTimeIcon fontSize="small" />,
      path: '/profile/settings?availability=true',
    });
    items.push({
      label: 'Arbitrator Dashboard',
      icon: <GavelOutlinedIcon fontSize="small" />,
      path: '/arbitrations/mine',
    });
  }

  if (role === 'ADMIN') {
    items.push({
      label: 'Deal Management',
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      path: '/admin/deals',
    });
    items.push({
      label: 'Dispute Management',
      icon: <GavelOutlinedIcon fontSize="small" />,
      path: '/admin/disputes',
    });
    items.push({
      label: 'Arbitrators Review',
      icon: <SecurityIcon fontSize="small" />,
      path: '/admin/arbitrators',
    });
  }

  return items;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Auth state ──────────────────────────────────────────────────────────────
  const [isLoggedIn,     setIsLoggedIn]     = useState(!!getAccessToken());
  const [role,           setRole]           = useState(localStorage.getItem('cmp_role') || '');
  const [accountStatus,  setAccountStatus]  = useState(localStorage.getItem('cmp_account_status') || '');
  const [isKycComplete,  setIsKycComplete]  = useState(localStorage.getItem('cmp_kyc_complete') === 'true');
  const [avatarUrl,      setAvatarUrl]      = useState('');

  const [themeMode, setThemeMode] = useState(localStorage.getItem('cmp_theme_mode') || 'dark');

  const toggleTheme = () => {
    const nextMode = themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem('cmp_theme_mode', nextMode);
    setThemeMode(nextMode);
    window.dispatchEvent(new Event('cmp-theme-change'));
  };

  useEffect(() => {
    const syncTheme = () => {
      setThemeMode(localStorage.getItem('cmp_theme_mode') || 'dark');
    };
    window.addEventListener('cmp-theme-change', syncTheme);
    return () => window.removeEventListener('cmp-theme-change', syncTheme);
  }, []);

  useEffect(() => {
    const sync = async () => {
      setIsLoggedIn(!!getAccessToken());
      setRole(localStorage.getItem('cmp_role') || '');
      setAccountStatus(localStorage.getItem('cmp_account_status') || '');
      setIsKycComplete(localStorage.getItem('cmp_kyc_complete') === 'true');
      
      if (getAccessToken()) {
        try {
          const { data } = await apiClient.get('/api/v1/auth/users/me/data/');
          setAvatarUrl(data.avatar_url || '');
          if (data.role) {
            setRole(data.role);
            localStorage.setItem('cmp_role', data.role);
          }
        } catch {
          setAvatarUrl('');
        }
      } else {
        setAvatarUrl('');
      }
    };
    sync();
    window.addEventListener('auth-state-change', sync);
    return () => window.removeEventListener('auth-state-change', sync);
  }, []);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [loggingOut,      setLoggingOut]      = useState(false);
  const [snack,           setSnack]           = useState({ open: false, message: '', severity: 'success' });
  const [unreadCount,     setUnreadCount]     = useState(
    parseInt(localStorage.getItem('cmp_unread_notifications') || '0', 10)
  );

  // Desktop Profile dropdown anchor
  const [profileAnchor,  setProfileAnchor]   = useState(null);
  const profileOpen = Boolean(profileAnchor);

  // Mobile drawer
  const [drawerOpen,     setDrawerOpen]      = useState(false);
  // Mobile Profile sub-list expand
  const [profileExpanded, setProfileExpanded] = useState(false);

  // ── Derived flags ───────────────────────────────────────────────────────────
  // KYC button: show when logged in and KYC is not completed (only for Buyer & Vendor roles)
  const showKycUpload     = isLoggedIn && !isKycComplete && (role === 'BUYER' || role === 'BIDDER');
  const showNotifications = isLoggedIn && (role === 'BUYER' || role === 'BIDDER' || role === 'ADMIN' || role === 'ARBITRATOR');
  const anonId            = localStorage.getItem('cmp_anonymous_id') || '';
  const profileItems      = buildProfileItems(role, anonId);
  const hasProfile        = isLoggedIn && profileItems.length > 0;

  // ── Notification polling ────────────────────────────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    if (!getAccessToken()) return;
    try {
      const { data } = await apiClient.get('/api/v1/notifications/');
      const count = data.unread_count || 0;
      setUnreadCount(count);
      localStorage.setItem('cmp_unread_notifications', String(count));
    } catch { /* silently ignore */ }
  }, []);

  useEffect(() => {
    if (!showNotifications) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [showNotifications, fetchUnreadCount]);

  useEffect(() => {
    const handler = () => {
      setUnreadCount(parseInt(localStorage.getItem('cmp_unread_notifications') || '0', 10));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
    setProfileAnchor(null);
  }, [location.pathname]);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    setLoggingOut(true);
    setDrawerOpen(false);
    try {
      await apiClient.post('/api/v1/auth/logout/');
    } catch { /* ignore */ }
    clearSession();
    setLoggingOut(false);
    setSnack({ open: true, message: 'You have been logged out successfully.', severity: 'success' });
    setTimeout(() => navigate('/login'), 800);
  };

  // ── Mobile Drawer content ───────────────────────────────────────────────────
  const MobileDrawer = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: 270,
          bgcolor: '#0d1526',
          color: '#fff',
          pt: 1,
        },
      }}
    >
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <SecurityIcon sx={{ color: '#818cf8' }} />
        <Typography variant="h6" fontWeight={700} sx={{ cursor: 'pointer' }} onClick={() => { navigate('/'); setDrawerOpen(false); }}>
          CyberExchange
        </Typography>
        {role && (
          <Chip label={role} size="small" color="secondary" sx={{ ml: 'auto', fontWeight: 600, fontSize: 11 }} />
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <List dense disablePadding>

        {/* Profile section */}
        {hasProfile && (
          <>
            <ListItemButton
              onClick={() => setProfileExpanded((v) => !v)}
              sx={{ px: 2, py: 1.2, '&:hover': { bgcolor: 'rgba(99,102,241,0.15)' } }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: '#a5b4fc' }}>
                {avatarUrl ? (
                  <Avatar src={avatarUrl} sx={{ width: 24, height: 24 }} />
                ) : (
                  <AccountCircleIcon />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Profile"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              />
              {profileExpanded ? <ExpandLessIcon sx={{ color: '#a5b4fc' }} /> : <ExpandMoreIcon sx={{ color: '#a5b4fc' }} />}
            </ListItemButton>

            <Collapse in={profileExpanded} timeout="auto" unmountOnExit>
              <List dense disablePadding>
                {profileItems.map((item) => (
                  <ListItemButton
                    key={item.path}
                    onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                    selected={location.pathname === item.path}
                    sx={{
                      pl: 5, py: 1,
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.12)' },
                      '&.Mui-selected': { bgcolor: 'rgba(99,102,241,0.22)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: '#94a3b8' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 13, color: '#e2e8f0' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mx: 2 }} />
          </>
        )}



        {/* Notifications */}
        {showNotifications && (
          <ListItemButton
            onClick={() => { navigate('/notifications'); setDrawerOpen(false); }}
            sx={{ px: 2, py: 1.2, '&:hover': { bgcolor: 'rgba(99,102,241,0.15)' } }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: '#a5b4fc' }}>
              <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={unreadCount > 0 ? `Notifications (${unreadCount})` : 'Notifications'}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
            />
          </ListItemButton>
        )}

        {/* Logout */}
        {isLoggedIn && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mx: 2 }} />
            <ListItemButton
              onClick={handleLogout}
              disabled={loggingOut}
              sx={{ px: 2, py: 1.2, '&:hover': { bgcolor: 'rgba(239,68,68,0.12)' } }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: '#f87171' }}>
                {loggingOut ? <CircularProgress size={18} color="inherit" /> : <LogoutIcon />}
              </ListItemIcon>
              <ListItemText
                primary={loggingOut ? 'Logging out…' : 'Logout'}
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14, color: '#f87171' }}
              />
            </ListItemButton>
          </>
        )}

        {/* Not logged in */}
        {!isLoggedIn && (
          <>
            <ListItemButton onClick={() => { navigate('/login'); setDrawerOpen(false); }} sx={{ px: 2, py: 1.2 }}>
              <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
            </ListItemButton>
            <ListItemButton onClick={() => { navigate('/register'); setDrawerOpen(false); }} sx={{ px: 2, py: 1.2 }}>
              <ListItemText primary="Register" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
            </ListItemButton>
          </>
        )}
      </List>
    </Drawer>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {/* Brand */}
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: 'pointer', fontWeight: 700 }}
            onClick={() => navigate('/')}
          >
            CyberExchange
          </Typography>

          {/* Role chip — desktop */}
          {isLoggedIn && role && (
            <Chip
              label={role}
              size="small"
              color="secondary"
              sx={{ mr: 2, fontWeight: 600, textTransform: 'uppercase', display: { xs: 'none', sm: 'flex' } }}
            />
          )}

          {/* ── Desktop nav ── */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>

            {/* Theme Toggle */}
            <Tooltip title={themeMode === 'dark' ? 'Activate Light Mode' : 'Activate Dark Mode'}>
              <IconButton
                color="inherit"
                onClick={toggleTheme}
                sx={{
                  mr: 1,
                  transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'scale(1.15) rotate(45deg)',
                    color: themeMode === 'dark' ? '#fbbf24' : '#6366f1',
                    filter: `drop-shadow(0 0 8px ${themeMode === 'dark' ? 'rgba(251,191,36,0.5)' : 'rgba(99,102,241,0.5)'})`
                  }
                }}
              >
                {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            {/* Notification bell */}
            {showNotifications && (
              <Tooltip title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'Notifications'}>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/notifications')}
                  aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                  sx={{ mr: 0.5 }}
                >
                  <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Profile dropdown */}
            {hasProfile && (
              <>
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={(e) => setProfileAnchor(e.currentTarget)}
                  aria-controls={profileOpen ? 'profile-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={profileOpen}
                  startIcon={
                    avatarUrl ? (
                      <Avatar src={avatarUrl} sx={{ width: 28, height: 28, border: '1.5px solid rgba(255,255,255,0.8)' }} />
                    ) : (
                      <AccountCircleIcon sx={{ fontSize: 26 }} />
                    )
                  }
                  endIcon={profileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    mr: 1,
                    pl: '6px',
                    pr: 2,
                    py: '4px',
                    borderRadius: '50px',
                    textTransform: 'none',
                    borderColor: 'rgba(255,255,255,0.3)',
                    fontWeight: 600,
                    fontSize: '14px',
                    '&:hover': {
                      borderColor: '#818cf8',
                      bgcolor: 'rgba(129,140,248,0.06)',
                    },
                  }}
                >
                  Profile
                </Button>

                <Menu
                  id="profile-menu"
                  anchorEl={profileAnchor}
                  open={profileOpen}
                  onClose={() => setProfileAnchor(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      mt: 0.5,
                      minWidth: 210,
                      bgcolor: '#0d1526',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      '& .MuiMenuItem-root': {
                        color: '#e2e8f0',
                        fontSize: 14,
                        gap: 1.5,
                        py: 1.1,
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.18)' },
                        '&.Mui-selected': { bgcolor: 'rgba(99,102,241,0.28)' },
                      },
                    },
                  }}
                >
                  {profileItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() => { navigate(item.path); setProfileAnchor(null); }}
                      selected={location.pathname === item.path}
                    >
                      <Box sx={{ color: '#a5b4fc', display: 'flex' }}>{item.icon}</Box>
                      {item.label}
                    </MenuItem>
                  ))}
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  <MenuItem
                    onClick={() => { setProfileAnchor(null); handleLogout(); }}
                    sx={{ color: '#f87171 !important', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.12) !important' } }}
                  >
                    <Box sx={{ color: '#f87171', display: 'flex' }}><LogoutIcon fontSize="small" /></Box>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}



            {/* Login / Register when unauthenticated (Logout is in the Profile Menu dropdown) */}
            {!isLoggedIn && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button color="inherit" onClick={() => navigate('/login')} aria-label="Login">Login</Button>
                <Button variant="outlined" color="inherit" onClick={() => navigate('/register')} aria-label="Register">Register</Button>
              </Box>
            )}
          </Box>

          {/* ── Mobile: notification bell + hamburger ── */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            {/* Theme Toggle */}
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{
                mr: 0.5,
                transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'scale(1.15) rotate(45deg)',
                  color: themeMode === 'dark' ? '#fbbf24' : '#6366f1',
                }
              }}
            >
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            {showNotifications && (
              <IconButton color="inherit" onClick={() => navigate('/notifications')} sx={{ mr: 0.5 }}>
                <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <IconButton
              color="inherit"
              edge="end"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>

        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      {MobileDrawer}

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
